import { createClient } from "@supabase/supabase-js";

const supabaseUrl = (import.meta.env.VITE_PUBLIC_SUPABASE_URL as string) ?? "";
const supabaseAnonKey =
  (import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string) ?? "";

/**
 * 단일 Supabase 클라이언트.
 *
 * 중요: autoRefreshToken 을 false 로 둔다.
 * 라이브러리 내부의 "백그라운드 자동 갱신"은 실패 시 에러를
 * 처리되지 않은 promise 거부(unhandled rejection)로 던지는데, 이 때문에
 * 브라우저에 남은 오래된 refresh token 으로
 * "Invalid Refresh Token: Refresh Token Not Found" 오류가 페이지 로드 시
 * 콘솔/모니터링에 잡힌다. 그래서 내부 타이머는 끄고,
 * 세션 갱신은 우리가 직접(await + try/catch) 관리한다.
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: false,
    detectSessionInUrl: true,
  },
});

/** refresh token 관련 무효화 에러 메시지인지 판별한다. */
export function isInvalidRefreshTokenMessage(message: string): boolean {
  if (!message) return false;
  return /invalid refresh token|refresh token (not found|already used|revoked|invalid|expired)/i.test(
    message,
  );
}

/** 로컬 스토리지에 남은 Supabase 세션 토큰을 지운다. */
function removeStaleAuthStorage(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && /^sb-.*-auth-token/.test(key)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // 저장소 접근 불가 시 무시
  }
}

let clearingSession = false;

/** 만료/무효 세션을 정리한다. 네트워크 호출 없이 로컬만 비운다. */
export async function clearStaleAuthSession(): Promise<void> {
  if (clearingSession) return;
  clearingSession = true;
  try {
    removeStaleAuthStorage();
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // 무시
  } finally {
    clearingSession = false;
  }
}

/**
 * 안전망: 혹시 어디선가 refresh token 무효화 거부가 튀어나오면
 * 기본 로깅을 막고 로컬 세션을 정리한다.
 */
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const reason: unknown = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
          ? reason
          : String((reason as { message?: string } | null)?.message ?? "");
    if (isInvalidRefreshTokenMessage(message)) {
      event.preventDefault();
      void clearStaleAuthSession();
    }
  });
}